import { loadStripe } from '@stripe/stripe-js';
import paymentService from '../services/paymentService';

// Initialize Stripe with the publishable key
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
};

/**
 * Creates a payment intent on the server with order references
 * @param {number} amount - Amount in cents
 * @param {string} orderId - Order ID (if available)
 * @param {Object} orderDetails - Additional order details
 * @param {string} currency - Currency code (default: 'usd')
 * @returns {Promise<Object>} Payment intent client secret and ID
 */
export const createPaymentIntent = async (amount, orderId = null, orderDetails = {}, currency = 'usd') => {
  try {
    // Use the payment service to create a payment intent
    const paymentIntent = await paymentService.createPaymentIntent(
      amount,
      orderId,
      {
        ...orderDetails.metadata,
        customer_id: orderDetails.customerId,
        customer_email: orderDetails.customerEmail,
        items_count: orderDetails.itemsCount,
        source: 'kiosk_checkout',
        kiosk_id: process.env.REACT_APP_KIOSK_ID || 'default-kiosk'
      }
    );

    return {
      clientSecret: paymentIntent.client_secret,
      paymentIntentId: paymentIntent.id,
      ...paymentIntent
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Confirms a card payment using Stripe Elements
 * @param {Object} stripe - Stripe instance
 * @param {Object} elements - Stripe Elements instance
 * @param {string} clientSecret - Payment intent client secret
 * @param {Object} billingDetails - Billing details for the payment
 * @returns {Promise<Object>} Payment result
 */
export const confirmCardPayment = async (stripe, elements, clientSecret, billingDetails = {}) => {
  if (!stripe || !elements) {
    throw new Error('Stripe.js has not loaded yet');
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    throw submitError;
  }

  const { error, paymentIntent } = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: window.location.origin + '/payment/complete',
      payment_method_data: {
        billing_details: {
          name: billingDetails.name || '',
          email: billingDetails.email || '',
          phone: billingDetails.phone || '',
          address: {
            line1: billingDetails.line1 || '',
            line2: billingDetails.line2 || '',
            city: billingDetails.city || '',
            state: billingDetails.state || '',
            postal_code: billingDetails.postalCode || '',
            country: billingDetails.country || 'US',
          },
        },
      },
    },
    redirect: 'if_required',
  });

  if (error) {
    throw error;
  }

  return paymentIntent;
};

/**
 * Handles the payment submission process with order reference
 * @param {Object} elements - Stripe Elements instance
 * @param {Object} billingDetails - Billing details for the payment
 * @param {number} amount - Amount in cents
 * @param {string} orderId - WooCommerce order ID
 * @param {Object} orderDetails - Additional order details
 * @returns {Promise<Object>} Payment result
 */
export const handlePayment = async (elements, billingDetails, amount, orderId, orderDetails = {}) => {
  try {
    // 1. Create payment intent on the server with order reference
    const { clientSecret, paymentIntentId } = await createPaymentIntent(
      amount, 
      orderId,
      {
        customerId: billingDetails?.customerId || '',
        customerEmail: billingDetails?.email || '',
        itemsCount: orderDetails?.items?.length || 0,
        metadata: {
          ...orderDetails.metadata,
          source: 'kiosk_checkout'
        }
      }
    );
    
    // 2. Get Stripe instance
    const stripe = await getStripe();
    
    // 3. Confirm the payment without capturing (we'll capture after order is confirmed)
    const result = await confirmCardPayment(stripe, elements, clientSecret, {
      ...billingDetails,
      payment_method_options: {
        card: {
          request_three_d_secure: 'any' // Enable 3D Secure if required
        }
      },
      confirm: true,
      off_session: false,
      setup_future_usage: 'off_session', // Allow future payments
      return_url: window.location.origin + '/payment/complete',
    });

    if (result.error) {
      throw result.error;
    }
    
    // Return the payment intent in the correct state (requires_capture)
    return {
      success: true,
      paymentIntent: result.paymentIntent,
      requiresAction: result.paymentIntent.status === 'requires_action',
      clientSecret: result.paymentIntent.client_secret,
      message: 'Payment authorized. Please complete the order to capture payment.'
    };
  } catch (error) {
    console.error('Payment error:', error);
    return {
      success: false,
      error: error.message || 'Payment failed. Please try again.',
    };
  }
};

/**
 * Formats card errors for display
 * @param {Object} error - Error object from Stripe
 * @returns {string} Formatted error message
 */
export const formatStripeError = (error) => {
  if (error.code === 'card_declined') {
    return 'Your card was declined.';
  }
  
  if (error.type === 'validation_error') {
    return 'Please check your card details and try again.';
  }
  
  return error.message || 'An error occurred while processing your payment.';
};

/**
 * Validates card details before submission
 * @param {Object} elements - Stripe Elements instance
 * @returns {boolean} True if card is valid, false otherwise
 */
export const validateCard = (elements) => {
  const cardElement = elements.getElement('card');
  if (!cardElement) return false;
  
  const { error } = cardElement;
  return !error;
};

/**
 * Gets the payment method type from the card element
 * @param {Object} elements - Stripe Elements instance
 * @returns {Promise<string>} Payment method type (e.g., 'card')
 */
export const getPaymentMethodType = async (elements) => {
  const { paymentMethod } = await elements.submit();
  return paymentMethod?.type || 'card';
};

/**
 * Captures a previously authorized payment intent
 * @param {string} paymentIntentId - The payment intent ID to capture
 * @param {Object} metadata - Additional metadata to include
 * @returns {Promise<Object>} The captured payment intent
 */
const capturePayment = async (paymentIntentId, metadata = {}) => {
  try {
    return await paymentService.capturePayment(paymentIntentId, metadata);
  } catch (error) {
    console.error('Error capturing payment:', error);
    throw error;
  }
};

/**
 * Cancels a payment intent
 * @param {string} paymentIntentId - The payment intent ID to cancel
 * @returns {Promise<Object>} The cancelled payment intent
 */
const cancelPaymentIntent = async (paymentIntentId) => {
  try {
    return await paymentService.cancelPaymentIntent(paymentIntentId);
  } catch (error) {
    console.error('Error cancelling payment intent:', error);
    throw error;
  }
};

export default {
  getStripe,
  createPaymentIntent,
  confirmCardPayment,
  handlePayment,
  formatStripeError,
  validateCard,
  getPaymentMethodType,
  capturePayment,
  cancelPaymentIntent
};
